/**
 * @file lighting.ts
 * @description GCOME_AP 灯光协议常量（参考 CH585M 的 ADDRESS_CH585M 单独维护风格）。
 *
 * 这些常量封装 GCOME_AP 功能变量区（GetFunc/SetFunc，CMD=0x05/0x06）的灯光字段偏移、
 * 灯效模式编码、灯光控制模块编码等协议细节，供 keyboard-lighting 与各灯效领域引用，
 * 不直接暴露到 SDK 公共接口。
 */

/** 功能变量区中灯光相关偏移量。 */
export const FUNC_OFFSET = {
  lightMode: 8,
  brightness: 9,
  speed: 10,
  direction: 11,
  colorfulFlag: 12,
  monoColorIndex: 13,
  r: 14,
  g: 15,
  b: 16,
  moduleSwitch: 23, // 0=背光, 1=侧灯, 2=Logo
  sideMode: 24,
  sideBrightness: 25,
  sideSpeed: 26,
  sideColorfulFlag: 27,
  sideColorIndex: 28,
  sideR: 29,
  sideG: 30,
  sideB: 31,
} as const

/** 背光灯效模式（文档枚举 0-22，0xff=关闭）。 */
export const LIGHT_MODE = {
  custom: 0,
  spectrum: 1,
  gradient: 2,
  static: 3,
  breathe: 4,
  flower: 5,
  wave: 6,
  waveUpr: 7,
  fountain: 8,
  glisten: 9,
  vortex: 10,
  tide: 11,
  swing: 12,
  ripple: 13,
  rippleOn: 14,
  single: 15,
  cell: 16,
  knock: 17,
  across: 18,
  rain: 19,
  starry: 20,
  firework: 21,
  waveBar: 22,
  off: 0xff,
} as const

/**
 * 侧灯专用灯效模式（功能变量区偏移24，网页版独立编码，与背光 0-22 不同）：
 *   0=流光, 1=七彩循环, 2=呼吸, 3=常亮, 4=关闭
 */
export const SIDE_LIGHT_MODE = {
  flow: 0,
  cycle: 1,
  breathe: 2,
  static: 3,
  off: 4,
} as const

/** 背光/侧灯可切换的灯光控制模块。 */
export const LIGHT_MODULE = {
  backlight: 0,
  side: 1,
  logo: 2,
} as const

/** 功能变量区总大小（64 字节）。 */
export const FUNC_BLOCK_SIZE = 64

/** 自定义灯光定义区总大小（512 字节）。 */
export const LED_DEFINE_SIZE = 512

/** 按键矩阵定义区单层大小（128 键 × 3 字节 + 48 字节 G 键 + 预留，共 512 字节）。 */
export const KEY_MATRIX_SIZE = 512

/** 最大按键数（每键 3 字节 RGB）。 */
export const KEY_COUNT = 128

/** 单帧 RGB 字节数（56 字节 payload 中最多容纳 18 键 = 54 字节）。 */
export const SYNC_BYTES_PER_FRAME = 54

/** 单帧可同步的按键数。 */
export const SYNC_KEYS_PER_FRAME = SYNC_BYTES_PER_FRAME / 3

/**
 * APP 自行管理的数据区（1024 字节）中，保存"背光/侧灯关闭前模式"的起始偏移。
 * 数据为 JSON（按板载下标区分多板载），置于区首，后续为宏元数据表（0x0060 起）。
 * 偏移分配参见 docs/app-define-region.md。
 */
export const LIGHT_STATE_APP_OFFSET = 0x0000

/** 未记录/未保存的灯效状态标记。 */
export const LIGHT_STATE_UNSET = 0xff
